import { Delete } from '@mui/icons-material';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { Dish, DishComponent } from '../../../apiTypes';
import FormPriceField from '../../../components/forms/FormPriceField';
import PrimaryButton from '../../../components/PrimaryButton';
import { t } from '../../../constants/translations';
import { useLocale } from '../../../contexts/locale.context';
import VariationComponents from './VariationComponents';

type Props = {
  form: UseFormReturn<Dish>;
  type: 'variations' | 'modifications';
  price: number;
  requiredComponents?: DishComponent[];
};

const VariationsField = ({ form, type, price, requiredComponents = [] }: Props) => {
  const { lang } = useLocale();
  const variations = form.watch(type);

  const {
    fields: variationFields,
    append: appendVariation,
    remove: removeVariation,
  } = useFieldArray({
    control: form.control,
    name: type,
    keyName: 'key',
  });

  return (
    <>
      {variationFields.map((variationField, index) => {
        const variation = variations?.[index];
        return (
          <Box key={variationField.key} display="flex" width="100%">
            <Typography color={(t) => t.palette.text.secondary} sx={{ mt: 1, mr: 2 }}>
              {index + 1}
            </Typography>

            <Box sx={{ width: '100%', mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  color="secondary"
                  label={t.name_of_variation[lang]}
                  fullWidth
                  sx={{ mb: 0.5 }}
                  error={!!form.formState.errors[type]?.[index]?.name}
                  {...form.register(`${type}.${index}.name`, {
                    required: true,
                  })}
                />

                {type === 'modifications' && (
                  <Box sx={{ width: 140 }}>
                    <FormPriceField
                      canBeNegative
                      label={t.price[lang]}
                      size="small"
                      name={`modifications.${index}.price`}
                    />
                  </Box>
                )}
              </Box>

              <VariationComponents
                form={form}
                variationIndex={index}
                components={variation?.components || []}
                requiredComponents={requiredComponents}
                price={variation && 'price' in variation ? (variation.price || 0) + price : price}
                type={type}
              />
            </Box>

            <IconButton
              sx={{ width: 32, height: 32, ml: 1, flexShrink: 0 }}
              size="small"
              onClick={() => removeVariation(index)}
            >
              <Delete />
            </IconButton>
          </Box>
        );
      })}
      <PrimaryButton size="small" onClick={() => appendVariation({ name: '', components: [] })}>
        {type === 'variations' ? t.add_variation[lang] : t.add_modification[lang]}
      </PrimaryButton>
    </>
  );
};

export default VariationsField;
